import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Sparkles, BookOpen, ClipboardList } from "lucide-react";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import PlanFilter from "./PlanFilter";
import PlanCreateModal from "./PlanCreateModal";
import PlanViewModal from "./PlanViewModal";
import PlanWorkflowCard from "./PlanWorkflowCard";
import { getPlans, deletePlan } from "../../../api/hrm/plans/info";
import { hasPermission } from "../../../utils/permissionUtils";
import { startPlanListTour } from "./planListTour";
import { getDefaultPlanFilters } from "./planFilterDefaults";

const STATUS_LABEL = {
  draft: "Nháp",
  pending: "Chờ xử lý",
  waiting_approval: "Chờ phê duyệt",
  approved: "Đã duyệt",
  rejected: "Từ chối",
};

const TYPE_MAP = {
  finance: { label: "Tài chính", color: "bg-indigo-50 text-indigo-800 border-indigo-100" },
  work: { label: "Công việc", color: "bg-emerald-50 text-emerald-800 border-emerald-100" },
};

const CATEGORY_MAP = {
  branch: { label: "Chi nhánh" },
  department: { label: "Phòng ban" },
  personal: { label: "Cá nhân" },
};

const norm = (s) => (s || "").toLowerCase();

const planStatusBadge = (status) => {
  const st = norm(status);
  const label = STATUS_LABEL[st] || status;
  const baseClass =
    "inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border";

  switch (st) {
    case "draft":
      return (
        <span className={`${baseClass} bg-gray-50 text-gray-600 border-gray-200`}>
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-gray-400" />
          {label}
        </span>
      );
    case "pending":
    case "waiting_approval":
      return (
        <span className={`${baseClass} bg-orange-50 text-orange-700 border-orange-100`}>
          <span className="mr-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-orange-500" />
          {label}
        </span>
      );
    case "approved":
      return (
        <span className={`${baseClass} bg-green-50 text-green-700 border-green-100`}>
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-500" />
          {label}
        </span>
      );
    case "rejected":
      return (
        <span className={`${baseClass} bg-red-50 text-red-700 border-red-100`}>
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-red-500" />
          {label}
        </span>
      );
    default:
      return <span className={baseClass}>{label}</span>;
  }
};

const PlanListPage = () => {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [plans, setPlans] = useState([]);
  const [filters, setFilters] = useState(() => getDefaultPlanFilters());
  const fetchSeqRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const canApprovePlans = hasPermission("hrm.plans", "A");

  const fetchPlans = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const res = await getPlans({
        ...filters,
        page: 1,
        pageSize: 500,
      });
      if (seq !== fetchSeqRef.current) return;
      setPlans(res.items || []);
      setTotalRecords(res.total || 0);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("Lỗi fetchPlans:", err);
      showErrorToast("Không thể tải danh sách kế hoạch");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setFirstLoadDone(true);
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const waitingPlans = plans.filter((p) =>
    ["pending", "waiting_approval"].includes(norm(p.status))
  );
  const approvedPlans = plans.filter((p) => norm(p.status) === "approved");
  const rejectedPlans = plans.filter((p) => norm(p.status) === "rejected");
  const draftPlans = plans.filter((p) => norm(p.status) === "draft");

  const getDashboardCount = (key) => {
    switch (key) {
      case "waiting":
        return waitingPlans.length;
      case "approved":
        return approvedPlans.length;
      case "rejected":
        return rejectedPlans.length;
      case "draft":
        return draftPlans.length;
      default:
        return 0;
    }
  };

  const handleOpenManual = useCallback(() => {
    const guideUrl = `${window.location.origin}/guides/hrm-plans-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handleDeleteDraft = async (plan) => {
    const ok = await confirmDelete({
      type: DELETE_ENTITY.plan_draft,
      name: plan.title || plan.id,
    });
    if (!ok.isConfirmed) return;
    try {
      await deletePlan(plan.id);
      showSuccessToast("Đã xóa kế hoạch");
      fetchPlans();
      setSelectedPlanId(null);
    } catch (e) {
      console.error(e);
      showErrorToast("Không thể xóa kế hoạch");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("vi-VN");
  };

  const formatCurrency = (value) => {
    if (value == null || value === "") return "0 đ";
    return new Intl.NumberFormat("vi-VN").format(Number(value)) + " đ";
  };

  const PlanGroup = ({ title, items, variant, emptyHint }) => {
    if (!firstLoadDone || loading) {
      return (
        <section className="mb-8 px-1 sm:px-0" data-tour={`hrm-plans-group-${variant}`}>
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-40 animate-pulse rounded-2xl border border-gray-50 bg-gray-100"
              />
            ))}
          </div>
        </section>
      );
    }
    if (!items.length) return null;
    return (
      <section className="mb-8 px-1 sm:px-0" data-tour={`hrm-plans-group-${variant}`}>
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-lg font-black text-gray-700">{title}</h3>
          <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600">
            {items.length} kế hoạch
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((plan) => (
            <PlanWorkflowCard
              key={plan.id}
              plan={plan}
              variant={variant}
              currentUserId={currentUser?.id}
              canApprove={variant === "waiting" && canApprovePlans}
              onSelect={() => setSelectedPlanId(plan.id)}
            />
          ))}
        </div>
        {emptyHint && (
          <p className="mt-2 text-center text-xs text-amber-700">{emptyHint}</p>
        )}
      </section>
    );
  };

  const truncatedTotal =
    totalRecords > plans.length ? (
      <p className="mb-3 text-center text-xs text-amber-800">
        Đang hiển thị {plans.length} / {totalRecords} bản ghi trong bộ lọc. Thu hẹp tháng hoặc lọc
        chi nhánh để xem đầy đủ trên thẻ.
      </p>
    ) : null;

  return (
    <div className="p-4">
      <div
        className="mb-8 flex items-center justify-between gap-3"
        data-tour="hrm-plans-header"
      >
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-red-700 p-2.5 text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon icon={faCalendarCheck} className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight text-gray-800">
              Quản lý <span className="text-red-700">kế hoạch</span>
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{plans.length}</span>{" "}
                kế hoạch đang hiển thị
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenManual}
            title="Hướng dẫn sử dụng"
            aria-label="Hướng dẫn sử dụng"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            <BookOpen className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => window.setTimeout(() => startPlanListTour(), 80)}
            title="Hướng dẫn nhanh"
            aria-label="Hướng dẫn nhanh"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-red-200 bg-red-50/90 text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100"
          >
            <Sparkles className="h-4 w-4 shrink-0 text-red-600" strokeWidth={2.25} aria-hidden />
          </button>
        </div>
      </div>

      <div data-tour="hrm-plans-filter">
        <PlanFilter
          onNewClick={() => setShowDrawer(true)}
          onFilterChange={(f) => setFilters(f)}
        />
      </div>

      <div className="my-6" data-tour="hrm-plans-dashboard">
        {isMobile ? (
          <div className="flex items-center justify-between gap-1 overflow-hidden rounded-full border border-gray-100 bg-white p-1 shadow-sm">
            {[
              { key: "waiting", icon: faClock, tailwind: "text-amber-600 bg-amber-50" },
              { key: "approved", icon: faCheckCircle, tailwind: "text-green-600 bg-green-50" },
              { key: "rejected", icon: faTimesCircle, tailwind: "text-red-600 bg-red-50" },
              { key: "draft", icon: faPenToSquare, tailwind: "text-gray-600 bg-gray-50" },
            ].map((item) => {
              const isActive = selectedDashboard === item.key;
              const count = getDashboardCount(item.key);
              const [textColor, bgColor] = item.tailwind.split(" ");
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() =>
                    setSelectedDashboard((prev) => (prev === item.key ? null : item.key))
                  }
                  className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 outline-none transition-all duration-300 ${
                    isActive ? `${bgColor} scale-105 shadow-inner` : "hover:bg-gray-50 active:scale-95"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className={`text-lg ${isActive ? textColor : "text-gray-400"}`}
                  />
                  <span
                    className={`text-base font-bold leading-none ${isActive ? textColor : "text-gray-600"}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="my-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { key: "waiting", label: "Chờ duyệt", icon: faClock, color: "text-amber-500" },
              { key: "approved", label: "Đã duyệt", icon: faCheckCircle, color: "text-green-600" },
              { key: "rejected", label: "Từ chối", icon: faTimesCircle, color: "text-red-500" },
              { key: "draft", label: "Nháp", icon: faPenToSquare, color: "text-gray-600" },
            ].map((item) => {
              const isActive = selectedDashboard === item.key;
              const count = getDashboardCount(item.key);
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() =>
                    setSelectedDashboard((prev) => (prev === item.key ? null : item.key))
                  }
                  className={`flex w-full cursor-pointer items-center gap-4 rounded-3xl border p-4 text-left transition-all duration-200 ${
                    isActive
                      ? "scale-[1.02] border-2 border-red-200 bg-red-50/50 shadow-lg"
                      : "border border-gray-200 bg-white hover:scale-[1.01] hover:shadow-md"
                  }`}
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border bg-gray-50 ${item.color.replace("text", "border")}`}
                  >
                    <FontAwesomeIcon icon={item.icon} className={`${item.color} text-xl`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{item.label}</p>
                    <p className={`text-lg font-semibold ${item.color}`}>{count}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {truncatedTotal}

      {selectedPlanId && (
        <PlanViewModal
          planId={selectedPlanId}
          onClose={() => setSelectedPlanId(null)}
          onUpdated={fetchPlans}
          onRequestDeleteDraft={handleDeleteDraft}
        />
      )}

      {loading && !firstLoadDone ? (
        <div className="animate-pulse py-10 text-center text-gray-500">Đang tải kế hoạch…</div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.28 }}
          className="flex flex-col gap-2"
          data-tour="hrm-plans-sections"
        >
          {(selectedDashboard === null || selectedDashboard === "waiting") && (
            <PlanGroup
              title="Kế hoạch chờ phê duyệt"
              items={waitingPlans}
              variant="waiting"
            />
          )}
          {(selectedDashboard === null || selectedDashboard === "approved") && (
            <PlanGroup
              title="Kế hoạch đã duyệt"
              items={approvedPlans}
              variant="approved"
            />
          )}
          {(selectedDashboard === null || selectedDashboard === "rejected") && (
            <PlanGroup title="Kế hoạch bị từ chối" items={rejectedPlans} variant="rejected" />
          )}
          {(selectedDashboard === null || selectedDashboard === "draft") && (
            <PlanGroup title="Kế hoạch nháp" items={draftPlans} variant="draft" />
          )}
        </motion.div>
      )}

      <div data-tour="hrm-plans-table">
        <h2 className="mb-3 ml-2 mt-10 text-base font-semibold text-gray-700">
          Tra cứu nhanh (bảng) ({plans.length} kế hoạch)
        </h2>

        <div className="overflow-hidden rounded-[2rem] border border-gray-100 bg-white shadow-xl shadow-slate-200/50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] table-auto border-separate border-spacing-0 border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-slate-50/80 text-left backdrop-blur-sm">
                  <th className="min-w-[220px] border-b border-gray-100 p-5 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Người tạo
                  </th>
                  <th className="min-w-[260px] border-b border-gray-100 p-5 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Kế hoạch
                  </th>
                  <th className="min-w-[200px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Thời gian
                  </th>
                  <th className="min-w-[140px] border-b border-gray-100 p-5 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Ngân sách
                  </th>
                  <th className="min-w-[130px] border-b border-gray-100 p-5 text-center text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Trạng thái
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50">
                          <ClipboardList size={32} className="text-gray-200" strokeWidth={1.75} />
                        </div>
                        <p className="text-xs font-black uppercase tracking-widest text-gray-400">
                          Hiện chưa có kế hoạch nào trong bộ lọc.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => {
                    const type =
                      TYPE_MAP[norm(plan.plan_type)] || {
                        label: plan.plan_type,
                        color: "bg-gray-50 text-gray-700 border-gray-100",
                      };
                    const cat =
                      CATEGORY_MAP[norm(plan.plan_category)] || {
                        label: plan.plan_category || "—",
                      };
                    const titleInitial = (plan.title || "?").trim().charAt(0).toUpperCase() || "?";
                    const creatorName = (plan.creator_name || "").trim();
                    const creatorInitial = creatorName
                      ? creatorName.split(/\s+/).pop().charAt(0).toUpperCase()
                      : "?";
                    return (
                      <tr
                        key={plan.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedPlanId(plan.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedPlanId(plan.id);
                          }
                        }}
                        className="group cursor-pointer transition-all duration-300 hover:bg-red-50/40"
                      >
                        <td className="p-5">
                          <div className="flex items-center gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-xs font-black text-slate-600 shadow-sm">
                              {creatorInitial}
                            </div>
                            <div className="flex min-w-0 flex-col">
                              <div className="truncate text-sm font-black uppercase tracking-tight text-slate-700 transition-colors group-hover:text-red-600">
                                {creatorName || "—"}
                              </div>
                              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  {plan.branch_name || "—"}
                                </span>
                                <span className="text-[10px] text-slate-200">•</span>
                                <span className="text-[10px] font-bold italic text-slate-400">
                                  {plan.department_name || "—"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="flex items-center gap-4">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-xs font-black text-red-600 shadow-sm">
                              {titleInitial}
                            </div>
                            <div className="flex min-w-0 flex-col gap-1.5">
                              <div className="line-clamp-2 text-sm font-black uppercase leading-snug tracking-tight text-slate-700 transition-colors group-hover:text-red-600">
                                {plan.title || "—"}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="inline-flex rounded-lg border border-purple-100 bg-purple-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-purple-700">
                                  {cat.label}
                                </span>
                                <span
                                  className={`inline-flex rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-wide ${type.color}`}
                                >
                                  {type.label}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <div className="flex items-center justify-center gap-2">
                            <div className="text-center">
                              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                Từ
                              </div>
                              <div className="text-sm font-black text-slate-700">{formatDate(plan.start_date)}</div>
                            </div>
                            <div className="font-black text-slate-300">→</div>
                            <div className="text-center">
                              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                Đến
                              </div>
                              <div className="text-sm font-black text-slate-700">{formatDate(plan.end_date)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-5 text-right">
                          <div className="inline-flex items-center justify-end rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm font-black tabular-nums text-emerald-700 shadow-sm">
                            {formatCurrency(plan.budget)}
                          </div>
                        </td>
                        <td className="p-5 text-center">{planStatusBadge(plan.status)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isMobile && hasPermission("hrm.plans", "C") && (
        <button
          type="button"
          onClick={() => setShowDrawer(true)}
          title="Tạo kế hoạch"
          className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-red-700 text-2xl text-white shadow-lg transition hover:scale-105 active:scale-95"
        >
          +
        </button>
      )}

      <PlanCreateModal
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        onSaved={() => {
          setShowDrawer(false);
          fetchPlans();
        }}
      />
    </div>
  );
};

export default PlanListPage;
